/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import AppContext from "AppContext";
import { useDispatch } from "react-redux";
import { notifyError, notifySuccess, notifyInfo } from "redux/actions/Notifications";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Slide from "@material-ui/core/Slide";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Divider } from "@material-ui/core";
import Button from "components/common_components/Button";
import LoadingPage from "components/common_components/LoadingPage";
import { formatCurrency } from "utils/currency";
import { dateToString } from "utils/dateUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

function ApplyPaymentDialog({ open, onClose, payment, onApplySuccess }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useDispatch();

    const {
        api: {
            Management: { Customers },
        },
    } = useContext(AppContext);

    const [fileCases, setFileCases] = useState([]);
    const [amounts, setAmounts] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [appData, setAppData] = useState(null);

    const totalPaid = appData?.totalPaidUsd ?? payment?.amountPaidUsd ?? 0;
    const totalApplied = appData?.totalAppliedUsd ?? 0;
    const serverRemaining = appData?.remainingUsd ?? totalPaid;
    const applications = appData?.applications ?? [];

    const newAssignedTotal = Object.values(amounts).reduce((sum, val) => {
        const num = parseFloat(val);
        return sum + (isNaN(num) ? 0 : num);
    }, 0);

    const remaining = serverRemaining - newAssignedTotal;

    const hasValidAssignment = Object.entries(amounts).some(([, val]) => {
        const num = parseFloat(val);
        return !isNaN(num) && num >= 0.01;
    });

    const hasInvalidAmounts = Object.entries(amounts).some(([, val]) => {
        if (val === "" || val === undefined || val === null) return false;
        const num = parseFloat(val);
        return isNaN(num) || num < 0;
    });

    const isOverAssigned = remaining < -0.001;

    useEffect(() => {
        if (open && payment?.clientId) {
            loadData();
        }
        if (!open) {
            setFileCases([]);
            setAmounts({});
            setAppData(null);
        }
    }, [open]);

    function loadData() {
        setIsLoading(true);
        Customers.getPaymentApplications({
            clientId: payment.clientId,
            paymentId: payment.id,
            onSuccess: (data) => {
                setAppData(data);
                loadFileCases();
            },
            onError: () => {
                loadFileCases();
            },
        });
    }

    function loadFileCases() {
        Customers.getFileCases({
            id: payment.clientId,
            onSuccess: (data) => {
                setFileCases(data);
                setIsLoading(false);
            },
            onInfo: (info) => {
                dispatch(notifyInfo(info));
                setFileCases([]);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setFileCases([]);
                setIsLoading(false);
            },
        });
    }

    function onAmountChange(fileCaseId, value) {
        setAmounts((prev) => ({ ...prev, [fileCaseId]: value }));
    }

    function buildPayload() {
        return Object.entries(amounts)
            .filter(([, val]) => {
                const num = parseFloat(val);
                return !isNaN(num) && num >= 0.01;
            })
            .map(([fileCaseId, val]) => {
                const fileCase = fileCases.find((fc) => String(fc.fileCaseId) === String(fileCaseId));
                return {
                    clientPortalPaymentsId: payment.id,
                    clientId: payment.clientId,
                    fileCaseId: fileCase.fileCaseId,
                    module: fileCase.module,
                    fileNo: fileCase.fileNo,
                    amountToApply: parseFloat(val),
                };
            });
    }

    function onSubmitHandler() {
        if (!hasValidAssignment) {
            dispatch(notifyError("You must assign at least $0.01 to one file case."));
            return;
        }
        if (hasInvalidAmounts) {
            dispatch(notifyError("Please fix invalid amounts before submitting."));
            return;
        }
        if (isOverAssigned) {
            dispatch(notifyError("Total assigned exceeds the payment amount."));
            return;
        }

        const payload = buildPayload();

        setIsSubmitting(true);
        Customers.applyClientPortalPayment({
            clientId: payment.clientId,
            payload,
            onSuccess: () => {
                dispatch(notifySuccess("Payment applied successfully!"));
                setIsSubmitting(false);
                onClose();
                if (onApplySuccess) onApplySuccess();
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsSubmitting(false);
            },
        });
    }

    const canSubmit = hasValidAssignment && !hasInvalidAmounts && !isOverAssigned;

    return (
        <Dialog
            open={open}
            fullScreen={fullScreen}
            TransitionComponent={Transition}
            keepMounted
            maxWidth="md"
            fullWidth
            aria-labelledby="apply-payment-dialog-title"
        >
            <DialogTitle id="apply-payment-dialog-title">Apply Payment</DialogTitle>
            <DialogContent>
                <div className="apply-payment-dialog__summary">
                    <Typography variant="body2" color="textSecondary">
                        Remaining to assign
                    </Typography>
                    <Typography
                        variant="h4"
                        className="apply-payment-dialog__remaining"
                        style={{ color: remaining < 0 ? "#f44336" : "#155724" }}
                    >
                        {formatCurrency(remaining)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Total paid: {formatCurrency(totalPaid)} &mdash; Previously applied: {formatCurrency(totalApplied)} &mdash; New
                        assignment: {formatCurrency(newAssignedTotal)}
                    </Typography>
                </div>

                {isLoading && <LoadingPage />}

                {!isLoading && applications.length > 0 && (
                    <div className="apply-payment-dialog__history">
                        <Typography variant="subtitle2" gutterBottom>
                            Application History
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <strong>File No</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Module</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>Applied</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Date</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>By</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {applications.map((app) => (
                                        <TableRow key={app.id}>
                                            <TableCell>{app.fileNo}</TableCell>
                                            <TableCell>{app.module}</TableCell>
                                            <TableCell align="right">{formatCurrency(app.amount)}</TableCell>
                                            <TableCell>{dateToString(app.stampCreationDatetime)}</TableCell>
                                            <TableCell>{app.stampCreationUserName}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Divider style={{ margin: "16px 0" }} />
                    </div>
                )}

                {!isLoading && fileCases.length === 0 && (
                    <Typography variant="body1" style={{ marginTop: 16 }}>
                        No file cases found for this client.
                    </Typography>
                )}

                {!isLoading && fileCases.length > 0 && (
                    <>
                        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16 }}>
                            Assign amounts to file cases
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <strong>File No</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Module</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>Amount to Apply</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fileCases.map((fc) => (
                                        <TableRow key={fc.fileCaseId}>
                                            <TableCell>{fc.fileNo}</TableCell>
                                            <TableCell>{fc.module}</TableCell>
                                            <TableCell align="right" style={{ width: 180 }}>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    variant="outlined"
                                                    margin="none"
                                                    value={amounts[fc.fileCaseId] ?? ""}
                                                    onChange={(e) => onAmountChange(fc.fileCaseId, e.target.value)}
                                                    inputProps={{ min: 0, step: 0.01 }}
                                                    fullWidth
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button primary outline onClick={onClose}>
                    Cancel
                </Button>
                <Button primary onClick={onSubmitHandler} loading={isSubmitting} disabled={!canSubmit}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ApplyPaymentDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    payment: PropTypes.object,
    onApplySuccess: PropTypes.func,
};

ApplyPaymentDialog.defaultProps = {
    payment: null,
    onApplySuccess: undefined,
};

export default ApplyPaymentDialog;
